// ── WHICH COLOUR IS THE BACKGROUND? (pure) ───────────────────────────────────────────────────────
// Ryan 2026-07-29, on RIGAR-Performance-Logo.png: *"I can't figure out why when I upload this logo,
// it's not detecting the white as a background color we want to remove."*
//
// THE OLD RULE AND WHY IT BROKE. `autoKey` sampled the 1-pixel border ring and asked two questions —
// is it near-white (mean ≥225) and is it one flat colour (std ≤24)? That is a PROXY for "is there a
// background to remove", and the proxy assumes every upload has a margin. RIGAR's wordmark is trimmed
// flush to its canvas, so the ring runs straight through the letters: 50% of the top row and 24% of
// the bottom row are black ink, giving mean 190 / std 104.6. Both tests fail, the code concludes
// "photographic or gradient — escalate", and 59.7% of the image stays pure white. Insetting the ring
// makes it worse (std 124 at 8px), because you move deeper into the letterforms.
//   Measured across Ryan's 40 real logos, that rejected **6** — including one sitting on an 89%
// single-colour field. ~1 in 6, and biased the wrong way: it fails on tightly-cropped, well-prepared
// brand-kit exports and succeeds on sloppy screenshots with fat margins.
//
// THE NEW RULE asks the question directly instead of via a proxy: **is there a large, flat region of
// one colour that is connected to the edge of the frame?** That is objectively answerable — no taste
// in it — and it does not care where the artwork happens to sit inside its canvas, which is the entire
// failure mode above. Candidate colours come from the four corners plus the border's modal colour;
// each is flood-filled inward from the border and scored by how much of the frame it actually covers;
// the best one wins if it covers enough to be a background.
//
// It SUBSUMES the old behaviour rather than replacing it: a file with a clean white margin has white
// corners and a big white flood, so it still resolves to the same white key it always did. A genuine
// photograph has corners that disagree and no single colour that floods far, so it still escalates.
//
// Pure and DOM-free on purpose — it takes raw RGBA and returns a decision, so `tools/verify-bg-detect.mjs`
// can test the real function over real files instead of re-implementing it in a harness (which is how a
// checker quietly drifts from the thing it checks).

/** Euclidean RGB radius treated as "the same colour" — matches bgKey's own flood core. */
export const COLOR_KEY_STRICT = 42;
/** A channel value at/above which a colour reads "white" — picks the keep-interior-fills mode. */
export const NEAR_WHITE_CHANNEL = 225;
/**
 * How much of the frame an edge-connected field must cover to count as a background.
 * 5%: below this there is no background worth removing, and the downstream adoption gate
 * (`removedFrac > 0.04` in logo-step) is the second opinion. Deliberately generous — the cost of a
 * false positive is a keyed logo the buyer can un-key with one toggle; the cost of a false negative is
 * the RIGAR bug, a white box printed on a plate with no obvious way to fix it.
 */
export const BG_MIN_COVERAGE = 0.05;
/** Below this, four corners that disagree still can't veto a field this dominant (e.g. one corner
 *  clipped by ink). Above it, disagreement is meaningful. */
const CORNER_DOMINANT_COVERAGE = 0.35;

const near = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) <= COLOR_KEY_STRICT;

/** True when every channel is at/above the near-white cutoff. */
export function isNearWhite(c) {
  return c[0] >= NEAR_WHITE_CHANNEL && c[1] >= NEAR_WHITE_CHANNEL && c[2] >= NEAR_WHITE_CHANNEL;
}

/**
 * Share of the frame occupied by pixels within `COLOR_KEY_STRICT` of `key` AND reachable from the
 * border. Edge-connectedness is what separates a background from a large interior fill of the same
 * colour: the white inside a letter is not reachable from outside it.
 *
 * ⚠ ALREADY-TRANSPARENT PIXELS ARE PASSABLE BUT NOT COUNTED, and getting this wrong is a real
 * regression I shipped and caught (Bauers Logo 2). An alpha PNG can arrive with its corners already
 * cut out — rounded, or a mark on a dark field trimmed to a shape — and treating transparency as a
 * WALL leaves the flood unable to reach the field it is looking for: two of that file's four corners
 * are [0,0,0,0], the dark field behind them scored ~0% coverage, and it lost the "Remove the
 * background color" offer it used to get. Transparency is not an obstacle, it is background that has
 * already been removed, so the flood walks straight through it. This is the same intent the old border
 * sampler stated when it skipped transparent border pixels to "read the field colour straight through
 * the rounding" — kept, in a place where it now also helps the coverage measure.
 *   Not counted toward coverage, though: coverage has to mean "how much of the frame is THIS colour",
 * or an image that is 90% transparent would look like a 90% background of every candidate colour.
 *
 * Iterative stack flood (never recursion — a 256×256 frame is 65k pixels and would blow the stack).
 */
export function edgeFieldCoverage(data, w, h, key) {
  const n = w * h;
  // 0 = wall (real ink), 1 = the field colour (passable, counted), 2 = transparent (passable only)
  const cell = new Uint8Array(n);
  for (let q = 0; q < n; q++) {
    const i = q * 4;
    if (data[i + 3] <= 8) cell[q] = 2;
    else if (near([data[i], data[i + 1], data[i + 2]], key)) cell[q] = 1;
  }
  const seen = new Uint8Array(n);
  const stack = [];
  for (let x = 0; x < w; x++) { stack.push(x); stack.push((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { stack.push(y * w); stack.push(y * w + w - 1); }
  let hit = 0;
  while (stack.length) {
    const q = stack.pop();
    if (q < 0 || q >= n || seen[q] || !cell[q]) continue;
    seen[q] = 1;
    if (cell[q] === 1) hit++;
    const x = q % w;
    if (x > 0) stack.push(q - 1);
    if (x < w - 1) stack.push(q + 1);
    if (q >= w) stack.push(q - w);
    if (q < n - w) stack.push(q + w);
  }
  return hit / n;
}

/* ⛔ A PART-TRANSPARENT BORDER PIXEL OVER A SOLID ONE IS THE PICTURE'S OWN EDGE (WD 472, Mojoe's Gym).
   A PDF rasterised by pdf.js comes back with its outermost column/row half-transparent grey — the page
   edge anti-aliased against nothing. That grey won the border vote (350 of 976 border pixels) and was the
   ONLY candidate, so the dark field inside it (44% of the frame) was never tried and stayed on the plate as
   a black box Ryan read as an outline. So a part-transparent border pixel is read ONE STEP INWARD when the
   pixel there is solid. ⛔ Not "skip every part-transparent pixel": wharton-county's red field is
   see-through all the way in (alpha ~178) and that reading lost its key — netted over all 109 raster marks. */
const BORDER_SOLID = 250;
function borderSample(data, w, h, x, y) {
  let i = (y * w + x) * 4;
  if (!globalThis.__wdBorderAlphaOld && data[i + 3] >= 8 && data[i + 3] < BORDER_SOLID) {
    const ix = x === 0 ? 1 : (x === w - 1 ? w - 2 : x);
    const iy = y === 0 ? 1 : (y === h - 1 ? h - 2 : y);
    const j = (iy * w + ix) * 4;
    if (ix >= 0 && iy >= 0 && ix < w && iy < h && data[j + 3] >= BORDER_SOLID) i = j;
  }
  return i;
}

/** The most common border colour, quantised to 16-level buckets so JPEG noise doesn't split one
 *  colour across a hundred near-identical values. Returns the bucket's MEAN (not its centre) so the
 *  value stays true to the pixels. */
function modalBorderColor(data, w, h) {
  const buckets = new Map();
  const add = (x, y) => {
    const i = borderSample(data, w, h, x, y);
    if (data[i + 3] < 8) return;
    const k = ((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4);
    const b = buckets.get(k) || [0, 0, 0, 0];
    b[0] += data[i]; b[1] += data[i + 1]; b[2] += data[i + 2]; b[3] += 1;
    buckets.set(k, b);
  };
  for (let x = 0; x < w; x++) { add(x, 0); add(x, h - 1); }
  for (let y = 0; y < h; y++) { add(0, y); add(w - 1, y); }
  let best = null;
  buckets.forEach((b) => { if (!best || b[3] > best[3]) best = b; });
  if (!best) return null;
  return [Math.round(best[0] / best[3]), Math.round(best[1] / best[3]), Math.round(best[2] / best[3])];
}

/**
 * Decide what (if anything) to key out of an image.
 *
 * @param {Uint8ClampedArray|Uint8Array} data RGBA pixels
 * @param {number} w
 * @param {number} h
 * @returns {{color: number[]|null, field: number[], global: boolean, coverage: number, why: string}|null}
 *   `null` → no client-side key is appropriate (escalate to the server).
 *   `color` is `null` for the NEAR-WHITE path, which `bgKey` keys by brightness rather than by RGB
 *   distance — that asymmetry is bgKey's existing contract and is preserved deliberately.
 *   `field` is the measured background colour and is ALWAYS present, so the near-white path can set
 *   its brightness cutoff RELATIVE to the field it actually found instead of against a constant.
 *   `global` chooses whether enclosed pockets are cut too; the defaults are unchanged from the old
 *   rule (white keeps interior fills, a solid colour cuts them), because THAT decision is being
 *   reworked separately and mixing the two changes at once would make a regression unattributable.
 */
export function chooseBackgroundKey(data, w, h) {
  if (!w || !h || !data || data.length < w * h * 4) return null;
  const at = (x, y) => {
    const i = borderSample(data, w, h, x, y);
    return data[i + 3] < 8 ? null : [data[i], data[i + 1], data[i + 2]];
  };
  const corners = [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]].map(([x, y]) => at(x, y)).filter(Boolean);
  const candidates = [];
  const push = (c) => { if (c && !candidates.some((e) => near(e, c))) candidates.push(c); };
  corners.forEach(push);
  push(modalBorderColor(data, w, h));
  if (!candidates.length) return null; // fully transparent border — nothing opaque to key

  let best = null;
  for (const c of candidates) {
    const coverage = edgeFieldCoverage(data, w, h, c);
    if (!best || coverage > best.coverage) best = { color: c, coverage };
  }
  if (!best || best.coverage < BG_MIN_COVERAGE) return null; // no background worth removing

  // Corner AGREEMENT is a photograph veto, not a requirement. Four corners that match each other say
  // "flat field"; corners that disagree usually mean a photo — but not always, because a mark trimmed
  // flush can have ink sitting in one corner while the field is still overwhelmingly flat (RIGAR is
  // one row short of exactly this). So disagreement only vetoes below CORNER_DOMINANT_COVERAGE, where
  // the evidence for a background is weak enough that the corners are the better witness.
  const agree = corners.length === 4 && corners.every((c) => near(c, corners[0]));
  if (!agree && best.coverage < CORNER_DOMINANT_COVERAGE) return null;

  const white = isNearWhite(best.color);
  return {
    color: white ? null : best.color,
    // ⚠ THE MEASURED FIELD, ALWAYS — including on the near-white path where `color` is null.
    // `color` answers "what do I key by RGB distance"; `field` answers "what colour IS the
    // background", and the near-white path needs the second even though it declines the first.
    // Withholding it is what made bgKey fall back to an ABSOLUTE 238 cutoff against a field it had
    // already measured at 243 — see nearWhiteThresh() in ui/logo-step.js for what that cost.
    field: best.color,
    global: !white,
    coverage: best.coverage,
    why: `${white ? 'near-white' : `rgb(${best.color.join(',')})`} field covers `
      + `${(best.coverage * 100).toFixed(0)}% edge-connected`
      + `${agree ? '' : ' (corners disagree, overridden by coverage)'}`,
  };
}

// ── ENCLOSED POCKETS ─────────────────────────────────────────────────────────────────────────────
// What the "Let weight show through enclosed areas" choice actually changes. Measured here; the
// DECISION lives in logo-step (`eatsInk`).
//
// ⚠ A PLATE-AWARE DECISION RULE LIVED HERE ON 2026-07-29 AND WAS REVERTED THE SAME DAY. It compared
// the plate against the ink enclosing a pocket and kept the fill when they would merge. The premise
// was wrong: this app's remedy for a logo that merges with the plate is to INVERT THE INK, not to fill
// the pockets — so the merge resolves itself and keeping the fill instead turned a one-tone mark into a
// two-tone one, which broke the structure detector and the flatten downstream. See changelog 142. The
// measurement below survived because it is just a measurement; the rule did not.

export const POCKET_MAX_SHARE = 0.18;
/** A pocket at/above 1% of the mark is a real hole somebody can see, not an anti-aliasing sliver. */
export const POCKET_BIG_SHARE = 0.01;
/** Enclosed negative space (letter counters) comes in MULTIPLES; below this count the pockets are
 *  more likely one drawn shape plus debris. See pocketsReadAsCounters. */
export const POCKET_COUNTER_MIN_BIG = 3;
/** ⭐ A LETTER COUNTER IS THE BACKGROUND SHOWING THROUGH, SO IT IS THE BACKGROUND'S COLOUR (WD 487, Ryan
 *  2026-11-01, UX run 1). Drop Bear's cream fur, muzzle and claws keyed as "letter counters" because
 *  they sit inside the white key's 45-radius; so do spartan-birds' pure-white crest stripes and eye on
 *  its #F7F7F7 field. Each big pocket's MEAN colour is compared with the mean of the background the
 *  border flood reached: a counter lands 0-8.1 away (Hanover, Bluffton, Tigers, Vikings, Grand Hyatt,
 *  AHS, Frostburg), artwork 13.0+ (spartan-birds 13, Drop Bear 20). The bar sits in that gap; over the
 *  whole 131-mark corpus it flips exactly those two See-through defaults to Solid, both shown to Ryan
 *  (scratch probe `tools/scratch/pocket-tone.mjs`). The buyer can still pick See-through. */
export const POCKET_BG_TONE_MATCH = 10.5;
/** How far past a pocket's edge to sample the enclosing ink. Must clear the anti-aliased boundary —
 *  see the note in measureEnclosedPockets for the bug that taught this. */
const INK_PROBE_PX = 3;

const hexOfRgb = (c) => `#${c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('').toUpperCase()}`;

/**
 * Measure the enclosed same-colour pockets — the ONLY thing the show-through choice changes.
 *
 * @returns {{share:number, pocketHex:string, ringHex:string|null}|null} `share` is the pockets' area
 *   as a fraction of the frame; `ringHex` is the mean colour of the ink immediately around them, which
 *   is what the pocket has to contrast against. null when there are no pockets at all.
 */
export function measureEnclosedPockets(data, w, h, key) {
  const n = w * h;
  const cell = new Uint8Array(n); // 0 wall, 1 field colour, 2 transparent
  for (let q = 0; q < n; q++) {
    const i = q * 4;
    if (data[i + 3] <= 8) cell[q] = 2;
    else if (near([data[i], data[i + 1], data[i + 2]], key)) cell[q] = 1;
  }
  // Same border flood as edgeFieldCoverage — anything of the field colour it cannot reach is enclosed.
  const seen = new Uint8Array(n);
  const stack = [];
  for (let x = 0; x < w; x++) { stack.push(x); stack.push((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { stack.push(y * w); stack.push(y * w + w - 1); }
  while (stack.length) {
    const q = stack.pop();
    if (q < 0 || q >= n || seen[q] || !cell[q]) continue;
    seen[q] = 1;
    const x = q % w;
    if (x > 0) stack.push(q - 1);
    if (x < w - 1) stack.push(q + 1);
    if (q >= w) stack.push(q - w);
    if (q < n - w) stack.push(q + w);
  }
  let pockets = 0;
  let rr = 0, rg = 0, rb = 0, ring = 0;
  for (let q = 0; q < n; q++) {
    if (cell[q] !== 1 || seen[q]) continue;
    pockets++;
    // The ink ENCLOSING this pocket — probed along the four axes at `INK_PROBE_PX`, NOT at the
    // immediate neighbour.
    //   ⚠ Sampling the adjacent pixel is wrong and it fails in the one direction that matters. The
    // pixel touching a white pocket inside a BLACK letter is the anti-aliased blend of the two, i.e.
    // mid-grey. Measured on RIGAR that read #606060 for letters that are pure black — so a black
    // plate scored ΔE 37 against it and the rule called show-through "legible", when in reality the
    // pocket would become black inside black ink and vanish. That is precisely Ryan's mush case, and
    // the boundary bias hid it. Three pixels clears typical anti-aliasing at this sampling size.
    const x = q % w, y = (q - x) / w;
    for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const nx = x + dx * INK_PROBE_PX, ny = y + dy * INK_PROBE_PX;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const nq = ny * w + nx;
      if (cell[nq] !== 0) continue; // still field or transparent → not the ink we're after
      const i = nq * 4;
      rr += data[i]; rg += data[i + 1]; rb += data[i + 2]; ring++;
    }
  }
  if (!pockets) return null;
  return {
    share: pockets / n,
    pocketHex: hexOfRgb(key),
    ringHex: ring ? hexOfRgb([rr / ring, rg / ring, rb / ring]) : null,
  };
}

// ── POCKET SHAPE (2026-08-18) ────────────────────────────────────────────────────────────────────
// The See-through default cannot be decided by HOW MUCH the global key takes: CSI needs KEEP at
// 18.3% of the mark and Hanover needs CUT at 24.6% — they are in the wrong order, so no threshold
// separates them (the fourth predicate to die on that question; see the V2 plan's OPEN section).
// What separates them is WHAT SHAPE the taken ink is. Three populations, semantically different
// things wearing one name:
//
//   several MODERATE pockets, none dominant → letter counters (Hanover's H/C) → cut reads right
//   one huge pocket                         → a shape somebody drew (Coastal's face) → keep it
//   hundreds of slivers                     → anti-aliased linework (CSI, Webster)  → keep it
//
// Enclosed negative space comes in multiples, each a modest share of the mark; one big pocket is
// artwork; dust is just edges.
//
// ⛔ THE CONSTRUCTION MUST MATCH bgKey's, NOT measureEnclosedPockets's, because the answer predicts
// what the TOGGLE actually changes. Two deliberate differences from the function above:
//   · transparency is a WALL, not passable — bgKey's keep-mode flood only walks pixels of the key
//     colour, so a pocket sealed behind already-transparent pixels IS kept by flood and cut by
//     global. edgeFieldCoverage's walk-through-transparency is right for "is there a background",
//     wrong for "what does the toggle change".
//   · the match radius is the CALLER's (COLOR_KEY_STRICT for a colour key, WHITE_BG_RADIUS on the
//     near-white path) — bgKey keys the two paths at different radii and this must mirror it.
// Validated 2026-08-18 over the 17 client-keyed marks in the CFA corpus at bgKey's own 1024px
// working scale (tools/probe-pocket-shape.mjs): reproduces the calibration table's verdict on all
// eight named cases, and moves exactly one See-through default on the whole corpus — Hanover.

/**
 * Per-pocket size profile of the enclosed key-colour regions — what the See-through toggle cuts.
 *
 * @param {Uint8ClampedArray|Uint8Array} data RGBA pixels of the UNCROPPED working frame
 * @param {number} w
 * @param {number} h
 * @param {number[]} key colour whose pockets are measured (the measured field on the white path)
 * @param {number} radius match radius — pass the same one bgKey keys with
 * @returns {{pockets:number, bigCount:number, bigBgTone:number, largestShare:number, totalShare:number, markPx:number}|null}
 *   shares are of the MARK (opaque pixels the border flood leaves, pockets included);
 *   null when there is no mark or no pocket at all.
 */
export function measurePocketProfile(data, w, h, key, radius = COLOR_KEY_STRICT) {
  if (!w || !h || !data || data.length < w * h * 4 || !Array.isArray(key)) return null;
  const n = w * h;
  const r2 = radius * radius;
  const isBg = new Uint8Array(n);
  for (let q = 0; q < n; q++) {
    const i = q * 4;
    if (data[i + 3] <= 8) continue;
    const dr = data[i] - key[0], dg = data[i + 1] - key[1], db = data[i + 2] - key[2];
    if (dr * dr + dg * dg + db * db <= r2) isBg[q] = 1;
  }
  // Border flood through key-colour pixels only — everything else, transparency included, is a wall.
  const reach = new Uint8Array(n);
  const stack = [];
  const seed = (q) => { if (isBg[q] && !reach[q]) { reach[q] = 1; stack.push(q); } };
  for (let x = 0; x < w; x++) { seed(x); seed((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { seed(y * w); seed(y * w + w - 1); }
  while (stack.length) {
    const q = stack.pop();
    const x = q % w;
    if (x > 0) seed(q - 1);
    if (x < w - 1) seed(q + 1);
    if (q >= w) seed(q - w);
    if (q < n - w) seed(q + w);
  }
  let markPx = 0;
  for (let q = 0; q < n; q++) {
    if (data[q * 4 + 3] <= 8) continue;
    if (reach[q]) continue;
    markPx++;
  }
  if (!markPx) return null;
  // Connected components of the enclosed key-colour cells = the pockets (4-connected, iterative).
  const compSeen = new Uint8Array(n);
  const sizes = [];
  const means = [];
  for (let q0 = 0; q0 < n; q0++) {
    if (!isBg[q0] || reach[q0] || compSeen[q0]) continue;
    let size = 0, sr = 0, sg = 0, sb = 0;
    const cs = [q0];
    compSeen[q0] = 1;
    while (cs.length) {
      const q = cs.pop();
      size++;
      sr += data[q * 4]; sg += data[q * 4 + 1]; sb += data[q * 4 + 2];
      const x = q % w;
      for (const d of [x > 0 ? q - 1 : -1, x < w - 1 ? q + 1 : -1, q >= w ? q - w : -1, q < n - w ? q + w : -1]) {
        if (d < 0 || !isBg[d] || reach[d] || compSeen[d]) continue;
        compSeen[d] = 1;
        cs.push(d);
      }
    }
    sizes.push(size);
    means.push([sr / size, sg / size, sb / size]);
  }
  if (!sizes.length) return null;
  // The background the border flood reached — what a real counter shows through (POCKET_BG_TONE_MATCH).
  let or = 0, og = 0, ob = 0, on = 0;
  for (let q = 0; q < n; q++) {
    if (!reach[q]) continue;
    or += data[q * 4]; og += data[q * 4 + 1]; ob += data[q * 4 + 2]; on++;
  }
  const outside = on ? [or / on, og / on, ob / on] : key;
  let largest = 0, total = 0, bigCount = 0, bigBgTone = 0;
  sizes.forEach((s, k) => {
    total += s;
    if (s > largest) largest = s;
    if (s / markPx < POCKET_BIG_SHARE) return;
    bigCount++;
    const m = means[k];
    if (Math.hypot(m[0] - outside[0], m[1] - outside[1], m[2] - outside[2]) <= POCKET_BG_TONE_MATCH) bigBgTone++;
  });
  return {
    pockets: sizes.length,
    bigCount,
    bigBgTone,
    largestShare: largest / markPx,
    totalShare: total / markPx,
    markPx,
  };
}

/**
 * Do these pockets read as LETTER COUNTERS — several moderate holes, none dominant — rather than a
 * drawn shape or anti-aliasing dust? True → cutting them to the plate is what the artwork intends.
 * `POCKET_MAX_SHARE` is reused deliberately: it has encoded "a pocket this big is artwork, not a
 * counter" since the 2026-07-29 rule it survived, and the corpus leaves the bar in open space
 * (largest-pocket shares jump from 8.6% straight to 41.8% — nothing sits near it).
 */
export function pocketsReadAsCounters(profile) {
  return !!profile
    && profile.largestShare < POCKET_MAX_SHARE
    && profile.bigCount >= POCKET_COUNTER_MIN_BIG
    // Counters must be the background's own colour (WD 487); a profile from before the field counts all.
    && (profile.bigBgTone ?? profile.bigCount) >= POCKET_COUNTER_MIN_BIG;
}

// ── IS THE MARK MADE OF FREE-FLOATING SINGLE-TONE PIECES? (Ryan 2026-09-19) ──────────────────────
// *"In 2-tone logos where the 2 tones don't touch, as-in, we have one free-floating part of the logo
// which is one color, and another free-floating part of the logo which is another color (like we do
// here with the free-floating yellow and free-floating green sections), then we can easily turn the
// entire logo to the non-clashing tone in its entirety knowing that it's not going to break the
// logo."*  — the second exception to "keep the mark and outline it" (see `markRecolourAllowed`).
//
// ⛔ THE FIRST DRAFT OF THAT RULE ASKED THE TOUCH TABLE AND MEASURED ITS OWN IGNORANCE. `toneTouches`
// answers "do these two tones share a boundary", which sounds like the same question and is not: the
// table is null on every mark whose structure read came back low-confidence, and a missing row read
// as "they do not touch". All 17 corpus hits were table-less marks — including BOTH marks Ryan
// judged — so the rule reproduced his answers by luck and would have recoloured every structure-less
// two-tone mark in the corpus. What he described is not adjacency of tones, it is CONNECTED PIECES
// of ink, which is a different measurement and one that exists whatever the structure read did.
//
// ⚠ AND IT IS NOT `soloTones`, WHICH IS THE SAME WALK ASKING A STRICTER QUESTION. That flood counts
// a piece as one-toned only when its significant-tone SET has size 1, so a single stray pixel of the
// other tone makes a piece two-toned — which is why the corpus record says the Layton spartan is one
// two-toned component while Ryan looks at it and sees two free-floating sections. `dom` below is a
// SHARE, so 99% navy reads as a navy piece, and that is what his sentence is about.
//
// ⚠ AN ARTWORK-LEVEL MEASUREMENT, MEASURED ONCE AT UPLOAD. It is a property of the FILE — nothing
// here depends on the plate — so it is computed in the analysis pass and stored beside
// `logoStructure`, never re-derived at a call site (CLAUDE.md §0b).

/** A piece smaller than this share of the mark is a dot or a crumb, not one of its "sections". */
export const INK_PIECE_MIN_SHARE = 0.05;
/** How much of one tone a piece must be before it counts as "one color". Ryan's own examples land at
 *  99–100% and the marks he called KEEP at 48–78%, so the whole 80–99 band is empty. */
export const INK_PIECE_SOLID_SHARE = 0.80;
/** Alpha at/above which a pixel is ink — `analyzeStructure`'s own foreground bar, not a second one. */
const INK_PIECE_ALPHA = 128;

/**
 * Connected components of the mark's INK, each labelled with the tone it is mostly made of.
 *
 * @param {Uint8ClampedArray|Uint8Array} data RGBA pixels of the keyed artwork
 * @param {number} w
 * @param {number} h
 * @param {Array<{hex:string}>} tones the mark's tone census (`traceTones`), any order
 * @returns {Array<{share:number, hex:string, dom:number}>|null} pieces at/over `INK_PIECE_MIN_SHARE`
 *   of the mark, largest first; `dom` is the winning tone's share OF THAT PIECE. null with no ink.
 */
export function measureInkPieces(data, w, h, tones) {
  if (!data || !w || !h || !Array.isArray(tones) || tones.length < 2) return null;
  const n = w * h;
  const rgbs = tones.map((t) => {
    const s = String((t && t.hex) || '').replace('#', '');
    return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
  });
  if (rgbs.some((c) => c.some((v) => !Number.isFinite(v)))) return null;
  // Nearest tone per ink pixel. Squared RGB distance: the tones come from the census that labelled
  // these same pixels, so this is a re-attribution, not a fresh classification.
  const lab = new Int16Array(n).fill(-1);
  let markPx = 0;
  for (let q = 0; q < n; q++) {
    const i = q * 4;
    if (data[i + 3] < INK_PIECE_ALPHA) continue;
    markPx += 1;
    let best = 0, bd = Infinity;
    for (let k = 0; k < rgbs.length; k++) {
      const dr = data[i] - rgbs[k][0], dg = data[i + 1] - rgbs[k][1], db = data[i + 2] - rgbs[k][2];
      const d = dr * dr + dg * dg + db * db;
      if (d < bd) { bd = d; best = k; }
    }
    lab[q] = best;
  }
  if (!markPx) return null;
  const seen = new Uint8Array(n);
  const stack = new Int32Array(n);
  const pieces = [];
  for (let s = 0; s < n; s++) {
    if (seen[s] || lab[s] < 0) continue;
    let sp = 0;
    stack[sp++] = s; seen[s] = 1;
    let size = 0;
    const cnt = new Array(tones.length).fill(0);
    while (sp > 0) {
      const q = stack[--sp];
      size += 1; cnt[lab[q]] += 1;
      const x = q % w, y = (q / w) | 0;
      if (x > 0 && !seen[q - 1] && lab[q - 1] >= 0) { seen[q - 1] = 1; stack[sp++] = q - 1; }
      if (x < w - 1 && !seen[q + 1] && lab[q + 1] >= 0) { seen[q + 1] = 1; stack[sp++] = q + 1; }
      if (y > 0 && !seen[q - w] && lab[q - w] >= 0) { seen[q - w] = 1; stack[sp++] = q - w; }
      if (y < h - 1 && !seen[q + w] && lab[q + w] >= 0) { seen[q + w] = 1; stack[sp++] = q + w; }
    }
    if (size / markPx < INK_PIECE_MIN_SHARE) continue;
    let bi = 0;
    for (let k = 1; k < cnt.length; k++) if (cnt[k] > cnt[bi]) bi = k;
    pieces.push({ share: +(size / markPx).toFixed(4), hex: tones[bi].hex, dom: +(cnt[bi] / size).toFixed(4) });
  }
  pieces.sort((a, b) => b.share - a.share);
  return pieces;
}

/**
 * THE PIXELS OF EVERY FREE-FLOATING PIECE WHOSE ONE TONE IS `hex` — the mask behind Ryan's
 * carrolllynx rule (2026-09-22): *"if we teach the tool to ONLY change the free-floating
 * white-only portion of the logo, then that could be a potential fix."*
 *
 * Every recolour in the app before this one moves a TONE across the whole mark, and on Carroll
 * that is the thing he ruled out in the same breath: *"If we change the white everywhere, well
 * that's hard to do b/c it'll likely break the multi-colored part of the logo."* His mark has a
 * completely-white section and a multi-coloured section that also contains white; only the first
 * may move.
 *
 * ⛔ IT IS THE SAME FLOOD AS `measureInkPieces`, DELIBERATELY — same 4-neighbour walk, same
 * `INK_PIECE_ALPHA`, same nearest-tone attribution, same `INK_PIECE_MIN_SHARE` floor, same
 * dominance test. A second, subtly different component finder would put the repaint on pixels the
 * measurement never counted, and the rule would be answering about one shape and painting another.
 *
 * @param {Uint8ClampedArray} data RGBA of the artwork the repaint is about to run on
 * @param {number} w
 * @param {number} h
 * @param {Array<{hex:string}>} tones the mark's tone census — the same list `measureInkPieces` got
 * @param {string} hex the tone whose solid pieces are wanted
 * @param {number} [minDom] dominance floor; defaults to the app's own `INK_PIECE_SOLID_SHARE`
 * @param {number} [minShare] size floor; defaults to `measureInkPieces`' own. The PAINTER passes 0 —
 *   see `remapBandsFromImageData` (WD 459, the C/O/L/L of CARROLL fell under the floor).
 * @returns {Uint8Array|null} 1 where a qualifying piece owns the pixel
 */
export function pieceMaskForTone(data, w, h, tones, hex, minDom = INK_PIECE_SOLID_SHARE, minShare = INK_PIECE_MIN_SHARE) {
  if (!data || !w || !h || !Array.isArray(tones) || tones.length < 2) return null;
  const want = String(hex || '').toUpperCase();
  const rgbs = tones.map((t) => {
    const v = String((t && t.hex) || '').replace('#', '');
    return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
  });
  if (rgbs.some((c) => c.some((v) => !Number.isFinite(v)))) return null;
  const n = w * h;
  const lab = new Int16Array(n).fill(-1);
  let markPx = 0;
  for (let q = 0; q < n; q++) {
    const i = q * 4;
    if (data[i + 3] < INK_PIECE_ALPHA) continue;
    markPx += 1;
    let best = 0; let bd = Infinity;
    for (let k = 0; k < rgbs.length; k++) {
      const dr = data[i] - rgbs[k][0]; const dg = data[i + 1] - rgbs[k][1]; const db = data[i + 2] - rgbs[k][2];
      const d = dr * dr + dg * dg + db * db;
      if (d < bd) { bd = d; best = k; }
    }
    lab[q] = best;
  }
  if (!markPx) return null;
  const mask = new Uint8Array(n);
  const seen = new Uint8Array(n);
  const stack = new Int32Array(n);
  const comp = new Int32Array(n);
  let hit = false;
  for (let s = 0; s < n; s++) {
    if (seen[s] || lab[s] < 0) continue;
    let sp = 0; let cp = 0;
    stack[sp++] = s; seen[s] = 1;
    let size = 0;
    const cnt = new Array(tones.length).fill(0);
    while (sp > 0) {
      const q = stack[--sp];
      comp[cp++] = q;
      size += 1; cnt[lab[q]] += 1;
      const x = q % w; const y = (q / w) | 0;
      if (x > 0 && !seen[q - 1] && lab[q - 1] >= 0) { seen[q - 1] = 1; stack[sp++] = q - 1; }
      if (x < w - 1 && !seen[q + 1] && lab[q + 1] >= 0) { seen[q + 1] = 1; stack[sp++] = q + 1; }
      if (y > 0 && !seen[q - w] && lab[q - w] >= 0) { seen[q - w] = 1; stack[sp++] = q - w; }
      if (y < h - 1 && !seen[q + w] && lab[q + w] >= 0) { seen[q + w] = 1; stack[sp++] = q + w; }
    }
    if (size / markPx < minShare) continue;
    let bi = 0;
    for (let k = 1; k < cnt.length; k++) if (cnt[k] > cnt[bi]) bi = k;
    if (cnt[bi] / size < minDom) continue;
    if (String(tones[bi].hex || '').toUpperCase() !== want) continue;
    for (let k = 0; k < cp; k++) mask[comp[k]] = 1;
    hit = true;
  }
  return hit ? mask : null;
}

/** A shape below this share of the mark is keying noise, not artwork. Deliberately three orders
 *  below `INK_PIECE_MIN_SHARE`: the touch map's whole job is to see the small shapes the piece
 *  test throws away. */
const TONE_TOUCH_NOISE = 0.001;
/** How much of a shape a tone must own before it counts as PRESENT in it. Under this it is the
 *  anti-aliased fringe where two shapes abut, which is not the same thing as two colours sharing
 *  a shape — and reading it as contact is what made the wreath look like one black mass. */
const TONE_TOUCH_PRESENT = 0.02;

/**
 * WHICH TONES SHARE A SHAPE WITH WHICH — the measurement behind Ryan's neutral-touch rule
 * (2026-09-20): *"if the colour being touched is a neutral, black or white, then we can change the
 * neutral or change the colour."*
 *
 * ⛔ IT IS NOT `measureInkPieces` WITH A SMALLER BAR, AND THE DIFFERENCE IS THE POINT. That function
 * answers "what are this mark's big sections made of", so it drops every shape under
 * `INK_PIECE_MIN_SHARE` and labels each survivor with its DOMINANT tone. Both of those hide exactly
 * what this rule asks about:
 *   - Cherry Valley's wreath carries its second colour in fifteen laurel leaves, every one a solid
 *     pink and none of them touching the black. The biggest is 2.1% of the mark, so the piece test
 *     discards all fifteen and reports a one-colour mark. The pink was never judged attached; it
 *     was judged small.
 *   - A shape that is 95% black and 5% pink is reported as "black", so a second tone can vanish
 *     into a label.
 * So: flood the same connected ink, keep every shape above a noise floor, and record every tone
 * PRESENT in it rather than the one that wins it.
 *
 * ⚠ AN ARTWORK-LEVEL MEASUREMENT, like the piece test beside it — no plate is involved, so it is
 * computed once at upload and stored, never re-derived at a call site.
 *
 * @param {Uint8ClampedArray|Uint8Array} data RGBA pixels of the keyed artwork
 * @param {number} w
 * @param {number} h
 * @param {Array<{hex:string}>} tones the mark's tone census (`traceTones`), any order
 * @returns {Array<{hex:string, touches:string[]}>|null} per tone, the other tones it shares a shape
 *   with; an empty `touches` means that tone owns whole shapes and never part of one.
 */
export function measureToneTouch(data, w, h, tones) {
  if (!data || !w || !h || !Array.isArray(tones) || tones.length < 2) return null;
  const n = w * h;
  const rgbs = tones.map((t) => {
    const s = String((t && t.hex) || '').replace('#', '');
    return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
  });
  if (rgbs.some((c) => c.some((v) => !Number.isFinite(v)))) return null;
  const lab = new Int16Array(n).fill(-1);
  let markPx = 0;
  for (let q = 0; q < n; q++) {
    const i = q * 4;
    if (data[i + 3] < INK_PIECE_ALPHA) continue;
    markPx += 1;
    let best = 0, bd = Infinity;
    for (let k = 0; k < rgbs.length; k++) {
      const dr = data[i] - rgbs[k][0], dg = data[i + 1] - rgbs[k][1], db = data[i + 2] - rgbs[k][2];
      const d = dr * dr + dg * dg + db * db;
      if (d < bd) { bd = d; best = k; }
    }
    lab[q] = best;
  }
  if (!markPx) return null;

  const touch = tones.map(() => new Set());
  const seen = new Uint8Array(n);
  const stack = new Int32Array(n);
  for (let s = 0; s < n; s++) {
    if (seen[s] || lab[s] < 0) continue;
    let sp = 0;
    stack[sp++] = s; seen[s] = 1;
    let size = 0;
    const cnt = new Array(tones.length).fill(0);
    while (sp > 0) {
      const q = stack[--sp];
      size += 1; cnt[lab[q]] += 1;
      const x = q % w, y = (q / w) | 0;
      if (x > 0 && !seen[q - 1] && lab[q - 1] >= 0) { seen[q - 1] = 1; stack[sp++] = q - 1; }
      if (x < w - 1 && !seen[q + 1] && lab[q + 1] >= 0) { seen[q + 1] = 1; stack[sp++] = q + 1; }
      if (y > 0 && !seen[q - w] && lab[q - w] >= 0) { seen[q - w] = 1; stack[sp++] = q - w; }
      if (y < h - 1 && !seen[q + w] && lab[q + w] >= 0) { seen[q + w] = 1; stack[sp++] = q + w; }
    }
    if (size / markPx < TONE_TOUCH_NOISE) continue;
    const present = [];
    for (let k = 0; k < cnt.length; k++) if (cnt[k] / size >= TONE_TOUCH_PRESENT) present.push(k);
    for (const a of present) for (const b of present) if (a !== b) touch[a].add(tones[b].hex);
  }
  return tones.map((t, k) => ({ hex: t.hex, touches: [...touch[k]] }));
}

/**
 * IS THIS TONE A BODY, OR ONLY A STROKE? — the measurement behind rule 3 (Ryan on Chatham,
 * 2026-09-20): *"the green colour which is extracted for the weightfill colour seems like a bit of
 * a stretch… hardly seeing any green in the actual logo."* Every green pixel in that mark is a
 * keyline one or two pixels wide, and it is what the weight number gets painted in.
 *
 * Erode the tone by one pixel and see what is left. A hairline vanishes; a filled shape barely
 * notices. `survival` is the share of the tone's own pixels that survive.
 *
 * ⛔ SURVIVAL ALONE IS NOT THE DEFECT, and the corpus says so in Ryan's own answers: he called a
 * 1.3% survivor WRONG and a 1.6% survivor FINE, then 2.2% wrong and 2.7% fine. What separates them
 * is thin AND MINOR — Wellesley's white survives 1.6% but is 22% of the logo, a fifth of the mark
 * drawn as fine linework, and he passed it. So this reports both numbers and decides nothing; the
 * pairing lives at the call site in `fillCandidates`.
 *
 * @param {Uint8ClampedArray|Uint8Array} data RGBA pixels of the keyed artwork
 * @param {number} w
 * @param {number} h
 * @param {Array<{hex:string, share:number}>} tones the mark's tone census (`traceTones`)
 * @returns {Array<{hex:string, survival:number, share:number}>|null}
 */
export function measureToneSubstance(data, w, h, tones) {
  if (!data || !w || !h || !Array.isArray(tones) || tones.length < 2) return null;
  const n = w * h;
  const rgbs = tones.map((t) => {
    const s = String((t && t.hex) || '').replace('#', '');
    return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
  });
  if (rgbs.some((c) => c.some((v) => !Number.isFinite(v)))) return null;
  const lab = new Int16Array(n).fill(-1);
  for (let q = 0; q < n; q++) {
    const i = q * 4;
    if (data[i + 3] < INK_PIECE_ALPHA) continue;
    let best = 0, bd = Infinity;
    for (let k = 0; k < rgbs.length; k++) {
      const dr = data[i] - rgbs[k][0], dg = data[i + 1] - rgbs[k][1], db = data[i + 2] - rgbs[k][2];
      const d = dr * dr + dg * dg + db * db;
      if (d < bd) { bd = d; best = k; }
    }
    lab[q] = best;
  }
  const total = new Array(tones.length).fill(0);
  const kept = new Array(tones.length).fill(0);
  for (let q = 0; q < n; q++) {
    const k = lab[q];
    if (k < 0) continue;
    total[k] += 1;
    const x = q % w, y = (q / w) | 0;
    // an 8-neighbour erosion: an edge pixel of the artwork counts as eroded, the way a 3x3
    // minimum filter treats the border
    if (x === 0 || y === 0 || x === w - 1 || y === h - 1) continue;
    let solid = true;
    for (let dy = -1; dy <= 1 && solid; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (lab[q + dy * w + dx] !== k) { solid = false; break; }
      }
    }
    if (solid) kept[k] += 1;
  }
  return tones.map((t, k) => ({
    hex: t.hex,
    survival: total[k] ? +(kept[k] / total[k]).toFixed(4) : 0,
    share: typeof t.share === 'number' ? t.share : null,
  }));
}

/** WD 474: an enclosed hole the GLOBAL key cut is put back when at least this share of the ink just
 *  outside it (3-5 px out, past the defringe band) is itself near the key colour. Measured on every
 *  globally-keyed mark: real pockets (Carroll's letter counters, Hanover, the Blue Sky lettering,
 *  Buccaneers) sit at 0.00-0.04; Carroll's flecks in its near-black shield and round LYNX at 0.13-0.88. */
export const INK_POCKET_MIN_SHARE = 0.10;

/**
 * Put back the enclosed holes a global colour key punched through ink that is merely NEAR the key
 * colour (WD 474, Ryan on carrolllynx: *"Not sure why the logo got so fuzzy and blury"*). Carroll is a
 * 200 px file on a black field and its shield is near-black, so the global cut left ~90 pinholes; on
 * the black plate they vanish, on the cream plate each one is a cream fleck. A hole is restored when
 * enough of the ink ringing it is near-key ink (`INK_POCKET_MIN_SHARE`); a hole ringed only by ink far
 * from the key - a letter counter in white type - stays cut. Pixels come back from `orig` exactly.
 * ⚠ It cannot see the black gaps inside a white-toothed mouth: ringed only by white, they read as
 * counters. That is the This-or-That chooser's call, not a rule's.
 * @param {Uint8ClampedArray} px keyed RGBA (mutated)
 * @param {Uint8ClampedArray} orig the same pixels before keying
 * @param {number[]} key [r,g,b]
 * @param {number} haloR the key's halo radius (what counts as "near the key")
 * @returns {number} pixels restored
 */
export function restoreInkPockets(px, orig, w, h, key, haloR, minShare = INK_POCKET_MIN_SHARE) {
  const n = w * h;
  const clear = (p) => px[p * 4 + 3] < 128;
  const outside = new Uint8Array(n);
  const st = new Int32Array(n);
  let sp = 0;
  const seed = (p) => { if (!outside[p] && clear(p)) { outside[p] = 1; st[sp++] = p; } };
  for (let x = 0; x < w; x++) { seed(x); seed((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { seed(y * w); seed(y * w + w - 1); }
  while (sp) {
    const p = st[--sp]; const x = p % w;
    if (x > 0) seed(p - 1); if (x < w - 1) seed(p + 1); if (p >= w) seed(p - w); if (p < n - w) seed(p + w);
  }
  /* Ink within 2 px of the OUTSIDE background is the key's own fringe, not ink: a small letter's counter
     (Elite Edge's SPORTS PERFORMANCE) has strokes thin enough that rings 3-5 land on it, and counting it
     read those counters as near-key pockets. Such pixels are skipped from both counts. */
  const byOutside = new Uint8Array(n);
  for (let p = 0; p < n; p++) if (outside[p]) byOutside[p] = 1;
  for (let it = 0; it < 2; it++) {
    const prev = byOutside.slice();
    for (let p = 0; p < n; p++) {
      if (prev[p]) continue;
      const x = p % w, y = (p / w) | 0;
      for (let dy = -1; dy <= 1 && !byOutside[p]; dy++) for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx, ny = y + dy;
        if (nx >= 0 && ny >= 0 && nx < w && ny < h && prev[ny * w + nx]) { byOutside[p] = 1; break; }
      }
    }
  }
  const h2 = haloR * haloR;
  const nearKey = (p) => {
    const i = p * 4, dr = px[i] - key[0], dg = px[i + 1] - key[1], db = px[i + 2] - key[2];
    return dr * dr + dg * dg + db * db <= h2;
  };
  const seen = new Uint8Array(n);
  const dist = new Uint8Array(n);
  const stamp = new Int32Array(n);
  let stampId = 0;
  let restored = 0;
  for (let s = 0; s < n; s++) {
    if (seen[s] || outside[s] || !clear(s)) continue;
    const comp = [];
    seen[s] = 1; st[0] = s; sp = 1;
    while (sp) {
      const p = st[--sp]; comp.push(p); const x = p % w;
      const nb = [x > 0 ? p - 1 : -1, x < w - 1 ? p + 1 : -1, p >= w ? p - w : -1, p < n - w ? p + w : -1];
      for (const q of nb) if (q >= 0 && !seen[q] && !outside[q] && clear(q)) { seen[q] = 1; st[sp++] = q; }
    }
    // Rings 1..5 out from the hole (8-neighbour BFS); the share is read on rings 3-5, past the fringe.
    stampId += 1;
    let frontier = comp;
    for (const p of comp) { stamp[p] = stampId; dist[p] = 0; }
    let ring = 0, near = 0;
    for (let d = 1; d <= 5; d++) {
      const next = [];
      for (const p of frontier) {
        const x = p % w, y = (p / w) | 0;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx, ny = y + dy;
          if ((!dx && !dy) || nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const q = ny * w + nx;
          if (stamp[q] === stampId) continue;
          stamp[q] = stampId; dist[q] = d; next.push(q);
          if (d >= 3 && !clear(q) && !byOutside[q]) { ring += 1; if (nearKey(q)) near += 1; }
        }
      }
      frontier = next;
    }
    if (!ring || near / ring < minShare) continue;
    for (const p of comp) {
      const i = p * 4;
      px[i] = orig[i]; px[i + 1] = orig[i + 1]; px[i + 2] = orig[i + 2]; px[i + 3] = orig[i + 3];
      restored += 1;
    }
  }
  return restored;
}
